import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api, platformLabel } from "../lib/api";
import ProductCard from "../components/ProductCard";
import Loading from "../components/Loading";
import { SlidersHorizontal, X, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { formatDateDisplay } from "../lib/utils";

const FILTERS = {
  category: [
    { v: "", l: "All" },
    { v: "consoles", l: "Consoles" },
    { v: "games", l: "Games" },
    { v: "accessories", l: "Accessories" },
    { v: "controllers", l: "Controllers" },
    { v: "digital", l: "Digital" },
  ],
  platform: [
    { v: "", l: "All" },
    { v: "ps5", l: "PS5" },
    { v: "ps4", l: "PS4" },
    { v: "switch 2", l: "Switch 2" },
    { v: "switch 1", l: "Switch 1" },
    { v: "xbox", l: "Xbox" },
  ],
  condition: [
    { v: "", l: "All" },
    { v: "new", l: "New" },
    { v: "used", l: "Used" },
    { v: "upcoming", l: "Pre-Book" },
  ],
};

const PAGE_SIZE = 40;

export default function SalePage() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [sale, setSale] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef(null);
  const sort = params.get("sort") || "relevant";
  const page = parseInt(params.get("page") || "1", 10);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const [saleRes, productsRes] = await Promise.all([
          api.get(`/sales/${slug}`),
          api.get(`/sales/${slug}/products`, {
            params: {
              category: params.get("category") || undefined,
              platform: params.get("platform") || undefined,
              condition: params.get("condition") || undefined,
              sort,
              page,
              page_size: PAGE_SIZE,
            },
          }),
        ]);
        const data = productsRes.data;
        const items = data.items ?? [];
        setSale(saleRes.data);
        setProducts(items);
        setTotal(data.total ?? items.length);
        setTotalPages(data.total_pages ?? Math.ceil((data.total ?? items.length) / PAGE_SIZE));
      } catch {
        setSale(null);
        setProducts([]);
        setTotal(0);
        setTotalPages(1);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, params, sort, page]);

  const setFilter = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    if (k !== "page") np.delete("page");
    setParams(np);
  };

  const setPage = (p) => {
    const np = new URLSearchParams(params);
    if (p === 1) np.delete("page"); else np.set("page", String(p));
    setParams(np);
  };

  const clearAll = () => {
    const np = new URLSearchParams();
    if (sort && sort !== "relevant") np.set("sort", sort);
    setParams(np);
  };

  const activeCount = ["category", "platform", "condition"].filter((k) => params.get(k)).length;
  const title = sale ? `${sale.name} - GamesTrade` : "Sale - GamesTrade";
  const dateRange = sale && (sale.starts_at || sale.ends_at)
    ? [sale.starts_at, sale.ends_at].filter(Boolean).map(formatDateDisplay).join(" - ")
    : "";

  if (!loading && notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h1 className="text-3xl font-black uppercase tracking-tight mb-3">Sale not found</h1>
        <p className="text-slate-400 mb-6">This sale is not active right now.</p>
        <Link to="/shop" className="inline-flex bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs px-5 py-2.5 rounded-full">
          Back to Shop
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={`Shop selected ${sale?.name || "sale"} deals at GamesTrade.`} />
      </Helmet>

      <section className="border-b border-gold/10 bg-navy-900">
        {sale?.banner_image ? (
          <div className="relative h-36 sm:h-44 lg:h-52 overflow-hidden">
            <img src={sale.banner_image} alt={sale.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-900/85 via-navy-900/25 to-navy-900/70" />
            <div className="relative site-container h-full flex flex-col justify-center">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-2">Sale</div>
              <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tighter">{sale.name}</h1>
              {dateRange && <div className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-white/80">{dateRange}</div>}
            </div>
          </div>
        ) : (
          <div className="site-container py-12">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-2">Sale</div>
            <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tighter">{sale?.name || "Sale"}</h1>
            {dateRange && <div className="mt-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">{dateRange}</div>}
          </div>
        )}
      </section>

      <div className="site-container py-10">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <button
            onClick={() => setPanelOpen((v) => !v)}
            className={`flex items-center gap-2 border rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wider transition ${
              panelOpen ? "bg-gold text-navy-900 border-gold" : "bg-navy-700 border-gold/20 hover:border-gold/50"
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {activeCount > 0 && (
              <span className={`text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center ${panelOpen ? "bg-navy-900 text-gold" : "bg-gold text-navy-900"}`}>
                {activeCount}
              </span>
            )}
            <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${panelOpen ? "rotate-180" : ""}`} />
          </button>

          {["category", "platform", "condition"].map((k) => {
            const v = params.get(k);
            if (!v) return null;
            const display = k === "platform" ? platformLabel(v) : v.charAt(0).toUpperCase() + v.slice(1);
            return (
              <button key={k} onClick={() => setFilter(k, "")} className="flex items-center gap-1.5 bg-gold/10 border border-gold/30 text-gold text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1.5 hover:bg-gold/20 transition">
                {display} <X className="w-3 h-3" />
              </button>
            );
          })}

          <div className="ml-auto text-xs text-slate-400 uppercase tracking-wider">{total} products</div>
        </div>

        <div ref={panelRef} className="overflow-hidden transition-all duration-300 ease-in-out" style={{ maxHeight: panelOpen ? "500px" : "0px", opacity: panelOpen ? 1 : 0 }}>
          <div className="bg-navy-700/60 border border-gold/15 rounded-2xl p-5 mb-6 backdrop-blur">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-6">
              {Object.entries(FILTERS).map(([key, opts]) => (
                <div key={key}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold/70 mb-2">{key}</div>
                  <div className="flex flex-col gap-0.5">
                    {opts.map((o) => {
                      const active = (params.get(key) || "") === o.v;
                      return (
                        <button key={o.v} onClick={() => setFilter(key, o.v)} className={`text-left text-sm px-3 py-1.5 rounded-md transition ${active ? "bg-gold text-navy-900 font-bold" : "text-slate-300 hover:bg-navy-600"}`}>
                          {o.l}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold/70 mb-2">Sort</div>
                <select value={sort} onChange={(e) => setFilter("sort", e.target.value)} className="w-full bg-navy-800 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold">
                  <option value="relevant">Relevant</option>
                  <option value="newest">Newest</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                  <option value="discount_high">Discount: High to Low</option>
                  <option value="discount_low">Discount: Low to High</option>
                </select>
              </div>
            </div>

            {activeCount > 0 && (
              <div className="mt-4 pt-4 border-t border-gold/10 flex items-center justify-between">
                <span className="text-xs text-slate-400">{activeCount} filter{activeCount > 1 ? "s" : ""} active</span>
                <button onClick={clearAll} className="text-xs uppercase tracking-wider text-rose-400 hover:text-rose-300 font-bold">
                  Clear all
                </button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20"><Loading variant="block" label="Loading" /></div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 text-slate-400">No products match your filters.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {products.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        )}

        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-10">
            <button onClick={() => setPage(page - 1)} disabled={page <= 1} className="flex items-center gap-1.5 border border-gold/30 rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wider transition disabled:opacity-30 hover:border-gold/60 hover:bg-navy-700">
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setPage(p)} className={`w-9 h-9 rounded-full text-sm font-bold transition ${p === page ? "bg-gold text-navy-900" : "text-slate-400 hover:bg-navy-700 hover:text-white"}`}>
                  {p}
                </button>
              ))}
            </div>
            <button onClick={() => setPage(page + 1)} disabled={page >= totalPages} className="flex items-center gap-1.5 border border-gold/30 rounded-full px-4 py-2 text-sm font-bold uppercase tracking-wider transition disabled:opacity-30 hover:border-gold/60 hover:bg-navy-700">
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
